import React from "react";
import {useTranslation} from "react-i18next";
import moment from "moment";
import {LogInterface} from "../../../../interfaces/private/LogInterface";
import LogStatusBadge from "./LogStatusBadge";

interface Props {
    logs: Array<LogInterface>;
    onView: (id: string | number) => void;
    onRetry?: (reference: string) => void;
    retryingReference?: string | null;
}

const LogTableBody = ({logs, onView, onRetry, retryingReference = null}: Props) => {
    const {t} = useTranslation();

    if (logs.length === 0) {
        return (
            <tbody>
            <tr>
                <td colSpan={5} className="py-5 text-center text-muted">
                    <i className="las la-inbox fs-24 d-block mb-2"/>
                    {t("log.no_logs")}
                </td>
            </tr>
            </tbody>
        );
    }

    return (
        <tbody className="list">
        {logs.map((log) => {
            const createdAt = moment(log.created_at);
            const openDetail = () => onView(log._id);

            return (
                <tr
                    key={String(log._id)}
                    role="button"
                    tabIndex={0}
                    className="cursor-pointer"
                    onClick={openDetail}
                    onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            openDetail();
                        }
                    }}
                >
                    <td className="text-break">{log.ref_id}</td>
                    <td><LogStatusBadge status={log.status} matchingStatus={log.data1}/></td>
                    <td className="text-truncate" style={{maxWidth: "420px"}} title={log.message || ""}>
                        {(log.message || "").substring(0, 100)}
                    </td>
                    <td title={createdAt.isValid() ? createdAt.format("LLLL Z") : ""}>
                        {createdAt.isValid() ? createdAt.format("HH:mm:ss DD.MM.YY") : "-"}
                    </td>
                    <td className="text-end">
                        {onRetry && log.status === "error" && log.ref_id && (
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-primary me-2"
                                aria-label={t("log.retry")}
                                disabled={retryingReference === log.ref_id}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    onRetry(log.ref_id);
                                }}
                            >
                                <i className={`las ${retryingReference === log.ref_id ? "la-spinner la-spin" : "la-redo-alt"} me-1`}/>
                                {t("log.retry")}
                            </button>
                        )}
                        <button
                            type="button"
                            className="btn btn-link p-0"
                            aria-label={t("log.view_detail")}
                            onClick={(event) => {
                                event.stopPropagation();
                                openDetail();
                            }}
                        >
                            <i className="las la-chevron-right fs-16 cl-br-ams ml-2"/>
                        </button>
                    </td>
                </tr>
            );
        })}
        </tbody>
    );
};

export default React.memo(LogTableBody);
